import React, { useState, useEffect } from 'react';
import MessageBoard from './MessageBoard';
import LoadingScreen from './LoadingScreen';
import api from './api';

const Communities = ({ onBack, user, onLoginRequired }) => {
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCommunity, setExpandedCommunity] = useState(null);
  const [boards, setBoards] = useState({});
  const [boardsLoading, setBoardsLoading] = useState({});
  const [selectedBoard, setSelectedBoard] = useState(null);
  const [selectedCommunity, setSelectedCommunity] = useState(null);

  useEffect(() => {
    if (user) {
      fetchMyCommunities();
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchMyCommunities = async () => {
    try {
      const response = await api.get('/communities');
      setCommunities(response.data);

      // Auto-expand if only one community
      if (response.data.length === 1) {
        const c = response.data[0];
        setExpandedCommunity(c.id);
        fetchBoards(c.id);
      }

      setLoading(false);
    } catch (error) {
      console.error('Error fetching communities:', error);
      setLoading(false);
    }
  };

  const fetchBoards = async (communityId) => {
    if (boards[communityId]) return;
    setBoardsLoading(prev => ({ ...prev, [communityId]: true }));
    try {
      const response = await api.get(`/communities/${communityId}/boards`);
      setBoards(prev => ({ ...prev, [communityId]: response.data }));
    } catch (error) {
      console.error('Error fetching boards:', error);
    } finally {
      setBoardsLoading(prev => ({ ...prev, [communityId]: false }));
    }
  };

  const toggleCommunity = (communityId) => {
    if (expandedCommunity === communityId) {
      setExpandedCommunity(null);
    } else {
      setExpandedCommunity(communityId);
      fetchBoards(communityId);
    }
  };

  const raceCommunities = communities.filter(c => c.type === 'race');
  const cityCommunities = communities.filter(c => c.type === 'city');

  if (selectedBoard && selectedCommunity) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#0B0B10',
        fontFamily: 'Inter, sans-serif'
      }}>
        {/* Header */}
        <div style={{
          background: '#0B0B10',
          padding: '1.5rem 2rem',
          borderBottom: '1px solid rgba(255, 138, 61, 0.15)'
        }}>
          <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <button
              onClick={() => { setSelectedBoard(null); setSelectedCommunity(null); }}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#FF8A3D',
                cursor: 'pointer',
                fontSize: '1.5rem',
                padding: '0.5rem'
              }}
            >
              ←
            </button>
            <div>
              <p style={{ color: '#8b9cb3', margin: 0, fontSize: '0.85rem' }}>
                {selectedCommunity.name}
              </p>
              <h1 style={{
                fontSize: '1.5rem',
                color: '#FFFFFF',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                {selectedBoard.icon} {selectedBoard.name}
              </h1>
            </div>
          </div>
        </div>

        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '2rem' }}>
          <MessageBoard
            board={selectedBoard}
            communityName={selectedCommunity.name}
            onBack={() => { setSelectedBoard(null); setSelectedCommunity(null); }}
            user={user}
            onLoginRequired={onLoginRequired}
          />
        </div>
      </div>
    );
  }

  if (loading) {
    return <LoadingScreen message="Loading your communities..." />;
  }

  const renderCommunitySection = (title, icon, communityList) => {
    if (communityList.length === 0) return null;

    return (
      <div style={{ marginBottom: '1.5rem' }}>
        <h3 style={{
          color: '#8b9cb3',
          fontSize: '0.85rem',
          fontWeight: 600,
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          marginBottom: '0.75rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          {icon} {title}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {communityList.map((community) => {
            const isExpanded = expandedCommunity === community.id;
            const communityBoards = boards[community.id] || [];
            const isLoadingBoards = boardsLoading[community.id];

            return (
              <div
                key={community.id}
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 138, 61, 0.15)',
                  borderRadius: '16px',
                  overflow: 'hidden'
                }}
              >
                {/* Community Header - Clickable */}
                <div
                  onClick={() => toggleCommunity(community.id)}
                  style={{
                    padding: '1.25rem 1.5rem',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: isExpanded ? 'rgba(255, 138, 61, 0.05)' : 'transparent',
                    transition: 'background 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    {community.image ? (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '10px',
                        background: `url(${community.image}) center/cover`,
                        flexShrink: 0
                      }} />
                    ) : (
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '10px',
                        background: community.type === 'city'
                          ? 'linear-gradient(135deg, #4ade80, #22c55e)'
                          : 'linear-gradient(135deg, #FF8A3D, #FF8A3D)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        flexShrink: 0
                      }}>
                        {community.type === 'city' ? '\u{1F3D9}\uFE0F' : '\u{1F3C1}'}
                      </div>
                    )}

                    <div>
                      <h3 style={{
                        color: '#FFFFFF',
                        fontSize: '1.1rem',
                        margin: 0,
                        fontWeight: 600,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.75rem'
                      }}>
                        {community.name}
                        {community.sport && community.type === 'city' && (
                          <span style={{
                            background: 'rgba(74, 222, 128, 0.15)',
                            color: '#4ade80',
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            padding: '0.2rem 0.5rem',
                            borderRadius: '10px',
                            textTransform: 'capitalize'
                          }}>
                            {community.sport}
                          </span>
                        )}
                      </h3>
                      <p style={{
                        color: '#8b9cb3',
                        fontSize: '0.85rem',
                        margin: '0.25rem 0 0 0'
                      }}>
                        {community.member_count} {community.member_count === 1 ? 'member' : 'members'}
                      </p>
                    </div>
                  </div>

                  <span style={{
                    color: '#FF8A3D',
                    fontSize: '1.25rem',
                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s ease'
                  }}>
                    ▼
                  </span>
                </div>

                {/* Expanded Boards */}
                {isExpanded && (
                  <div style={{
                    borderTop: '1px solid rgba(255, 138, 61, 0.1)',
                    padding: '0.5rem'
                  }}>
                    {isLoadingBoards ? (
                      <p style={{
                        color: '#8b9cb3',
                        padding: '1.5rem',
                        textAlign: 'center',
                        margin: 0
                      }}>
                        Loading boards...
                      </p>
                    ) : communityBoards.length === 0 ? (
                      <p style={{
                        color: '#8b9cb3',
                        padding: '1.5rem',
                        textAlign: 'center',
                        margin: 0
                      }}>
                        No message boards yet for this community.
                      </p>
                    ) : (
                      communityBoards.map((board) => (
                        <div
                          key={board.id}
                          onClick={() => {
                            setSelectedBoard(board);
                            setSelectedCommunity(community);
                          }}
                          style={{
                            padding: '1rem 1.25rem',
                            margin: '0.25rem',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            transition: 'background 0.2s ease'
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{ fontSize: '1.25rem' }}>{board.icon || '\u{1F4AC}'}</span>
                            <div>
                              <h4 style={{
                                color: '#FFFFFF',
                                margin: 0,
                                fontSize: '1rem',
                                fontWeight: 500
                              }}>
                                {board.name}
                              </h4>
                              <p style={{
                                color: '#6b7280',
                                margin: '0.15rem 0 0 0',
                                fontSize: '0.8rem'
                              }}>
                                {board.thread_count} {board.thread_count === 1 ? 'thread' : 'threads'}
                              </p>
                            </div>
                          </div>
                          <span style={{ color: '#FF8A3D', fontSize: '1rem' }}>{'\u2192'}</span>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0B0B10',
      fontFamily: 'Inter, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        background: '#0B0B10',
        padding: '1.5rem 2rem',
        borderBottom: '1px solid rgba(255, 138, 61, 0.15)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          display: 'flex',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <button
            onClick={onBack}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#FF8A3D',
              cursor: 'pointer',
              fontSize: '1.5rem',
              padding: '0.5rem'
            }}
          >
            ←
          </button>
          <h1 style={{
            fontSize: '2rem',
            fontFamily: 'Instrument Serif, serif',
            color: '#FFFFFF',
            margin: 0
          }}>
            Communities
          </h1>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        padding: '2rem'
      }}>
        {communities.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '4rem 2rem',
            color: '#8b9cb3'
          }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>{'\u{1F4AC}'}</div>
            <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No communities yet</h3>
            <p>Register for a race or set your city in Settings to join communities!</p>
            <button
              onClick={onBack}
              style={{
                marginTop: '1.5rem',
                padding: '0.75rem 1.5rem',
                background: '#FF8A3D',
                border: 'none',
                color: '#0B0B10',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: 600
              }}
            >
              Browse Races
            </button>
          </div>
        ) : (
          <div>
            <p style={{ color: '#8b9cb3', margin: '0 0 1.5rem 0' }}>
              {communities.length} {communities.length === 1 ? 'community' : 'communities'}
            </p>

            {renderCommunitySection('Your City', '\u{1F3D9}\uFE0F', cityCommunities)}
            {renderCommunitySection('Your Races', '\u{1F3C1}', raceCommunities)}
          </div>
        )}
      </div>
    </div>
  );
};

export default Communities;
